import React, { memo, useRef, useState } from "react";
import { FileSpreadsheet, Plus, Rocket, X } from "lucide-react";
import TaskCard from "./TaskCard";
import { positionAt } from "./boardState";
import { dragState } from "./dragState";
import SelectCheckbox from "./SelectCheckbox";

/**
 * A workflow column. Drag & drop is native HTML5 (as in the previous board):
 * cards carry their id in dataTransfer and the column reports drops upward.
 */
const BoardColumn = ({
  status,
  tasks,
  lookups,
  pendingIds,
  unreadIds,
  canDrag,
  canCreate,
  onDropTask,
  onOpenTask,
  onCreate,
  onExport,
  selection,
}) => {
  const [isOver, setIsOver] = useState(false);
  // Slot (among the other cards) where the dragged card would land.
  const [dropIndex, setDropIndex] = useState(null);
  const listRef = useRef(null);

  const slotFor = (clientY) => {
    const cards = [...(listRef.current?.querySelectorAll("[data-task-id]") || [])].filter(
      (el) => el.dataset.taskId !== dragState.taskId,
    );
    const i = cards.findIndex((el) => {
      const r = el.getBoundingClientRect();
      return clientY < r.top + r.height / 2;
    });
    return i === -1 ? cards.length : i;
  };

  return (
    <div
      className={`flex flex-col w-[280px] shrink-0 h-full rounded-[18px] border bg-panel2 backdrop-blur-xl transition-colors ${
        isOver ? "border-accent-line ring-2 ring-accent-soft" : "border-line"
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!isOver) setIsOver(true);
        const slot = slotFor(e.clientY);
        if (slot !== dropIndex) setDropIndex(slot);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsOver(false);
          setDropIndex(null);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        const slot = slotFor(e.clientY);
        setIsOver(false);
        setDropIndex(null);
        const taskId = e.dataTransfer.getData("taskId");
        if (taskId) onDropTask(taskId, status.value, positionAt(tasks, slot, taskId));
      }}
      aria-label={`${status.label} column`}
    >
      <header className="flex items-center justify-between gap-2 px-3.5 pt-3.5 pb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${status.accent}`} />
          <h2 className="font-display text-[14.5px] font-semibold text-ink truncate">{status.label}</h2>
          <span className="font-mono text-[11.5px] text-faint">{tasks.length}</span>
          {selection && tasks.length > 0 && (
            <SelectCheckbox
              checked={selection.selectedCount === tasks.length}
              indeterminate={selection.selectedCount > 0}
              onChange={selection.onToggleAll}
              label="Select all tasks"
              title={selection.selectedCount === tasks.length ? "Clear selection" : "Select all tasks"}
              className="ml-1 pl-1 pr-2 py-0.5 rounded-lg text-[11.5px] text-faint hover:text-accent hover:bg-accent-soft transition-colors"
            >
              All
            </SelectCheckbox>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {onExport && (
            <button
              type="button"
              onClick={() => onExport(status.value)}
              disabled={tasks.length === 0}
              className="p-1.5 rounded-lg text-faint hover:text-accent hover:bg-accent-soft disabled:opacity-40 disabled:pointer-events-none"
              aria-label={`Export ${status.label} to Excel`}
              title={tasks.length ? "Export to Excel" : "Nothing to export"}
            >
              <FileSpreadsheet className="w-4 h-4" />
            </button>
          )}
          {canCreate && (
            <button
              type="button"
              onClick={() => onCreate(status.value)}
              className="p-1.5 rounded-lg text-faint hover:text-accent hover:bg-accent-soft"
              aria-label={`Create task in ${status.label}`}
              title="New task"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-2.5 pb-2.5 space-y-2.5">
        {tasks.length === 0 ? (
          <p className="text-[12.5px] text-center text-faint py-6 rounded-xl border border-dashed border-line2 select-none">No tasks</p>
        ) : (
          (() => {
            // Orange line at the drop slot (slots skip the card being dragged).
            const line = <div key="drop-line" className="h-1 rounded-full bg-grad shadow-glow" aria-hidden="true" />;
            const out = [];
            let slot = 0;
            tasks.forEach((task) => {
              const isDragged = task._id === dragState.taskId;
              if (!isDragged && dropIndex === slot) out.push(line);
              out.push(
                <TaskCard
                  key={task._id}
                  task={task}
                  lookups={lookups}
                  draggable={canDrag(task)}
                  pending={pendingIds.has(task._id)}
                  hasUnread={unreadIds.has(task._id)}
                  selectable={Boolean(selection)}
                  selected={selection?.selectedIds.has(task._id)}
                  onToggleSelect={selection?.onToggle}
                  onOpen={onOpenTask}
                />,
              );
              if (!isDragged) slot += 1;
            });
            if (dropIndex !== null && dropIndex >= slot) out.push(line);
            return out;
          })()
        )}
      </div>

      {selection && selection.selectedCount > 0 && (
        <footer className="shrink-0 flex items-center gap-2 px-3 py-2.5 border-t border-line bg-panel-solid rounded-b-[18px]">
          <span className="text-[12.5px] text-muted flex-1 min-w-0 truncate">
            <strong className="text-ink">{selection.selectedCount}</strong> selected
          </span>
          <button
            type="button"
            onClick={selection.onClear}
            className="p-1.5 rounded-lg text-faint hover:text-ink hover:bg-hover"
            aria-label="Clear selection"
            title="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={selection.onCreateSprint}
            className="btn-primary !h-8 !px-3 !text-[12.5px] inline-flex items-center gap-1.5"
          >
            <Rocket className="w-3.5 h-3.5" /> Create Sprint
          </button>
        </footer>
      )}
    </div>
  );
};

export default memo(BoardColumn);
